<template>
  <form
    id="login-form"
    class="d-flex flex-column gap-6"
    @submit="onSubmit"
  >
    <h2 class="h4 text-center">
      {{ title }}
    </h2>

    <section class="d-flex flex-column gap-4">
      <GuFormField v-slot="{ componentField }" name="j_username">
        <GuFormItem id="username">
          <GuFormLabel>{{ usernameLabel }}</GuFormLabel>
          <GuFormControl>
            <GuInput
              type="text"
              v-bind="componentField"
              autocomplete="username"
            />
          </GuFormControl>
          <GuFormMessage />
        </GuFormItem>
      </GuFormField>

      <GuFormField v-slot="{ componentField }" name="j_password">
        <GuFormItem id="password">
          <GuFormLabel>Password</GuFormLabel>
          <GuInputGroup>
            <GuFormControl>
              <GuInput
                :type="showPassword ? 'text' : 'password'"
                v-bind="componentField"
                autocomplete="current-password"
              />
            </GuFormControl>
            <GuButton
              type="button"
              :icon="showPassword ? 'bxs-show' : 'bx-show'"
              class="btn-toggle"
              :title="showPassword ? 'Hide password' : 'Show password'"
              aria-label="Toggle password visibility"
              @click="showPassword = !showPassword"
            />
          </GuInputGroup>
          <GuFormMessage />
        </GuFormItem>
      </GuFormField>

      <GuFormField
        v-if="!dontRememberUsername"
        v-slot="{ value, handleChange }"
        type="checkbox"
        name="rememberMe"
      >
        <GuFormItem id="rememberMe" class="form-check">
          <GuFormControl>
            <GuCheckbox :checked="value" @update:checked="handleChange" />
          </GuFormControl>
          <GuFormLabel>Remember my username on this device</GuFormLabel>
        </GuFormItem>
      </GuFormField>
    </section>

    <GuAlert
      v-if="isLoginError"
      class="mb-0"
      variant="danger"
      icon="bxs-error"
    >
      <p>{{ errorMessage }}</p>
    </GuAlert>

    <GoogleCaptcha v-if="showCaptcha" />

    <GuButton type="submit" :class="submitClasses" :loading="isSubmitting">
      {{ submitLabel }}
    </GuButton>

    <div v-if="helpUrl" class="text-center">
      <a :href="helpUrl">{{ helpMessage }}</a>
    </div>
  </form>
</template>

<script setup>
import { computed } from 'vue'
import * as z from 'zod'

import GoogleCaptcha from '@shared/components/login/GoogleCaptcha.vue'
import {
  GuFormControl,
  GuFormField,
  GuFormItem,
  GuFormLabel,
  GuFormMessage,
} from '@shared/gu-components/form'
import { GuAlert } from '@shared/gu-components/alert'
import { GuButton } from '@shared/gu-components/button'
import { GuCheckbox } from '@shared/gu-components/checkbox'
import { GuInput } from '@shared/gu-components/input'
import { GuInputGroup } from '@shared/gu-components/input-group'

import { useLogin, useSharedLoginProps } from '@auth/composables/Login'

const props = defineProps({
  ...useSharedLoginProps(),
})

const formSchema = computed(() => ({
  j_username: z.string().min(1, { message: 'Username is required' }),
  j_password: z.string().min(1, { message: 'Password is required' }),
  rememberMe: z.boolean().default(false).optional(),
}))

const {
  errorMessage,
  isLoginError,
  isSubmitting,
  showCaptcha,
  showPassword,

  clearStoredFormData,
  handleSubmit,
  saveFormDataToStorage,
  submitForm,
} = useLogin(formSchema)

const onSubmit = handleSubmit((values) => {
  if (values.rememberMe) {
    saveFormDataToStorage()
  } else {
    clearStoredFormData()
  }

  submitForm(props.action)
})
</script>
